<template>
	<b-modal
		ref="specimen-specific-details-modal"
		:title="`Detalles de ${specimen.alias ? specimen.alias : '---'} (${specimen.plate})`"
		no-close-on-backdrop
		centered
		@hidden="handleHidden"
		size="md"
		hide-footer
	>
		<div>
			<div class="flex flex-column justify-content-center align-items-center mb-2">
				<b-badge
					v-if="specimen.plate"
					@click="linkSpecimen(specimen.id)"
					class="plate cursor-pointer mb-1"
					style="color: white; background: rgba(0, 0, 0, 0.7); font-size: 1.5rem"
				>
					{{ specimen.plate ? specimen.plate : "" }}
				</b-badge>
				<img
					onerror="this.src='/rooster/rooster.jpg'"
					class="rectangular-image"
					:src="specimen.thumb ? specimen.thumb : '/rooster/rooster.jpg'"
					:alt="specimen.plate"
				/>
			</div>
			<h5 class="mb-1 text-primary font-weight-bold">Información General</h5>
			<ul>
				<li v-if="specimen.padrillo.id">
					<strong>Padrillo:</strong>
					<span
						v-b-tooltip.hover
						:title="getStatusTitle(specimen.padrillo.is_in_galpon)"
						:class="{
							'cursor-pointer': padrilloHasDashboard,
							'text-success': specimen.padrillo.is_in_galpon === 1,
							'text-danger': specimen.padrillo.is_in_galpon === 0,
						}"
						@click="padrilloHasDashboard ? linkSpecimen(specimen.padrillo.id) : null"
					>
						{{ specimen.padrillo.description }}
					</span>
					<FeatherIcon
						v-if="padrilloHasDashboard"
						icon="EyeIcon"
						class="text-warning cursor-pointer"
						size="18"
						@click="openDetails(specimen.padrillo)"
					/>
				</li>
				<li v-if="specimen.madrilla.id">
					<strong>Madrilla:</strong>
					<span
						v-b-tooltip.hover
						:title="getStatusTitle(specimen.madrilla.is_in_galpon)"
						:class="{
							'cursor-pointer': madrillaHasDashboard,
							'text-success': specimen.madrilla.is_in_galpon === 1,
							'text-danger': specimen.madrilla.is_in_galpon === 0,
						}"
						@click="madrillaHasDashboard ? linkSpecimen(specimen.madrilla.id) : null"
					>
						{{ specimen.madrilla.description }}
					</span>
					<FeatherIcon
						v-if="madrillaHasDashboard"
						icon="EyeIcon"
						class="text-warning cursor-pointer"
						size="18"
						@click="openDetails(specimen.madrilla)"
					/>
				</li>
				<li v-if="[3, 5].includes(specimen.category_id) && specimen.topes">
					<strong>Topes:</strong>
					{{ specimen.topes || 0 }}
				</li>
				<!-- <li>
					<strong>Peleas:</strong>
					{{ specimen.peleas || 0 }}
				</li> -->
				<li v-if="specimen.torneos">
					<strong>Torneos:</strong>
					{{ specimen.torneos || 0 }}
				</li>
				<li>
					<strong>Peso:</strong>
					{{ specimen.kg_weight || "---" }} kg ({{ specimen.pounds_weight || "---" }} lbs)
				</li>
				<li>
					<strong>Calificación:</strong>
					{{ specimen.average_cap.toFixed(2) || "---" }}
				</li>
				<li>
					<strong>Ubicación:</strong>
					{{ formatLocationName(specimen.location_area_name, specimen.location_name) }}
				</li>
				<li v-if="specimen.sum_childs_season_now || specimen.global_childs">
					<strong>Hijos:</strong>
					<span v-b-tooltip title="Temporada" class="ml-1">T: {{ specimen.sum_childs_season_now }}</span>
					<span class="ml-05" v-b-tooltip title="Acumulado">A: {{ specimen.global_childs }}</span>
				</li>
				<li v-if="specimen.fertilidad_temporada || specimen.fertilidad_acumulada">
					<strong>Fertilidad:</strong>
					<span v-b-tooltip title="Temporada" class="ml-1">T: {{ specimen.fertilidad_temporada }}%</span>
					<span class="ml-05" v-b-tooltip title="Acumulado">A: {{ specimen.fertilidad_acumulada }}%</span>
				</li>
				<li v-if="[1, 2].includes(specimen.category_id)">
					<strong>% de hijos en Torneos:</strong>
					<b-badge variant="primary" class="ml-1 cursor-pointer" @click="showEffectivinessModal = true">
						{{ effectivity ? effectivity.toFixed(2) : 0 }}%
					</b-badge>
				</li>
			</ul>

			<!-- Cualidades -->
			<h5 v-if="specimen.qualities.length" class="mt-1 mb-1 text-primary font-weight-bold">Cualidades</h5>
			<ul>
				<li v-for="quality in specimen.qualities" :key="quality.id">
					<strong>{{ quality.name }}:</strong>
					{{ quality.percentage }}%
				</li>
			</ul>

			<!-- Características -->
			<h5 v-if="specimen.characteristics.length" class="mt-1 mb-1 text-primary font-weight-bold">
				Características
			</h5>
			<ul>
				<li v-for="characteristic in specimen.characteristics" :key="characteristic.id">
					<strong>{{ characteristic.name }}:</strong>
					{{ characteristic.percentage }}%
				</li>
			</ul>
		</div>
		<SpecimenSpecificDetailsModal
			v-if="showSpecificDetailsModal"
			:generalInfo="selectedSpecimen"
			@close="showSpecificDetailsModal = false"
		/>
		<b-modal v-model="showEffectivinessModal" title="Efectividad de hijos en torneos">
			<ChildsEffectivinessChart
				:specimen="{
					...specimen,
					category: specimen.category_id,
				}"
			/>
		</b-modal>
	</b-modal>
</template>

<script>
import specimensService from "@/services/specimens.service"
import SpecimenPlate from "@/components/specimens/SpecimenPlate.vue"
import SpecimenSpecificDetailsModal from "@/components/specimens/SpecimenSpecificDetailsModal.vue"
import ChildsEffectivinessChart from "@/views/amg/specimens/components/ChildsEffectivinessChart.vue"
import ConfrontationsResultService from "@/services/confrontationsResults.service"

export default {
	name: "SpecimenSpecificDetailsModal",
	components: {
		SpecimenPlate,
		SpecimenSpecificDetailsModal,
		ChildsEffectivinessChart,
	},
	props: {
		generalInfo: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			showEffectivinessModal: false,
			specimen: {
				topes: 0,
				peleas: 0,
				torneos: 0,
				kg_weight: 0,
				pounds_weight: 0,
				average_cap: 0,
				qualities: [],
				characteristics: [],
				padrillo: {
					id: null,
					description: "",
					is_in_galpon: null,
				},
				madrilla: {
					id: null,
					description: "",
					is_in_galpon: null,
				},
			},
			selectedSpecimen: null,
			showSpecificDetailsModal: false,
			effectivity: null,
		}
	},
	computed: {
		padrilloHasDashboard() {
			return (
				this.specimen.padrillo &&
				this.specimen.padrillo.id &&
				this.specimen.padrillo.status_id &&
				this.specimen.padrillo.category_id &&
				this.specimen.padrillo.origin_id
			)
		},
		madrillaHasDashboard() {
			return (
				this.specimen.madrilla &&
				this.specimen.madrilla.id &&
				this.specimen.madrilla.status_id &&
				this.specimen.madrilla.category_id &&
				this.specimen.madrilla.origin_id
			)
		},
	},
	methods: {
		async getEffectiviness() {
			const { data } = await ConfrontationsResultService.getSeasonConfrontations(
				this.specimen.category_id,
				this.specimen.id
			)
			let points = data.find((it) => it.id == 1).counter * 2 + data.find((it) => it.id == 2).counter
			let average = data.reduce((accumulator, currentValue) => accumulator + currentValue.counter, 0) * 2
			this.effectivity = (points / average) * 100
		},
		openDetails(specimen) {
			this.selectedSpecimen = specimen
			this.showSpecificDetailsModal = true
		},
		getStatusTitle(status) {
			switch (status) {
				case 1:
					return "Disponible"
				case 0:
					return "No disponible"
				default:
					return "Desconocido"
			}
		},
		linkSpecimen(id) {
			window.open(`/ejemplares/detalle/${id}`, "_blank")
		},
		handleHidden() {
			this.$emit("close")
		},
		async getDetails() {
			this.isPreloading()
			try {
				const { data } = await specimensService.getSpecimenSpecificInformation(this.generalInfo.id)
				this.specimen = data
				this.specimen.sum_childs_season_now = Number(this.specimen.sum_childs_season_now)
				this.specimen.global_childs = Number(this.specimen.global_childs)
				this.specimen.fertilidad_temporada = Number(this.specimen.fertilidad_temporada)
				this.specimen.fertilidad_acumulada = Number(this.specimen.fertilidad_acumulada)
				this.specimen.topes = Number(this.specimen.topes)
				this.specimen.torneos = Number(this.specimen.torneos)
			} catch (error) {
				console.log(error)
			} finally {
				this.isPreloading(false)
			}
		},
	},
	async mounted() {
		await this.getDetails()
		this.getEffectiviness()
		this.$refs["specimen-specific-details-modal"].toggle()
	},
}
</script>

<style lang="scss" scoped>
.rectangular-image {
	border-radius: 10%;
	width: 200px;
	height: 200px;
	object-fit: cover;
}
</style>
